import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, InputNumber, Row, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getSlowRequests, getSlowSql } from '../services/perf';
import type { SlowRequestItem, SlowRequestTopUrl, SlowSqlItem, SlowSqlTopQuery } from '../services/perf';

function fmtTs(ts: number) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function getErrorMessage(e: unknown) {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message || '获取失败';
  if (e && typeof e === 'object') {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return '获取失败';
}

const Perf: React.FC = () => {
  const [minutes, setMinutes] = useState(60);

  const [slowReqMinMs, setSlowReqMinMs] = useState(1000);
  const [slowReqLoading, setSlowReqLoading] = useState(false);
  const [slowReqError, setSlowReqError] = useState<string | null>(null);
  const [slowReqRecent, setSlowReqRecent] = useState<SlowRequestItem[]>([]);
  const [slowReqTopUrls, setSlowReqTopUrls] = useState<SlowRequestTopUrl[]>([]);

  const [slowSqlMinMs, setSlowSqlMinMs] = useState(500);
  const [slowSqlLoading, setSlowSqlLoading] = useState(false);
  const [slowSqlError, setSlowSqlError] = useState<string | null>(null);
  const [slowSqlRecent, setSlowSqlRecent] = useState<SlowSqlItem[]>([]);
  const [slowSqlTop, setSlowSqlTop] = useState<SlowSqlTopQuery[]>([]);

  const refreshSlowReq = async () => {
    setSlowReqLoading(true);
    try {
      const res = await getSlowRequests({ minutes, limit: 200, minDurationMs: slowReqMinMs });
      setSlowReqRecent(res.recent);
      setSlowReqTopUrls(res.topUrls);
      setSlowReqError(null);
      message.info(`读取日志：${res.file}（慢请求阈值 ${res.minDurationMs}ms）`, 2);
    } catch (e: unknown) {
      setSlowReqError(getErrorMessage(e));
    } finally {
      setSlowReqLoading(false);
    }
  };

  const refreshSlowSql = async () => {
    setSlowSqlLoading(true);
    try {
      const res = await getSlowSql({ minutes, limit: 200, minDurationMs: slowSqlMinMs });
      setSlowSqlRecent(res.recent);
      setSlowSqlTop(res.topQueries);
      setSlowSqlError(null);
      message.info(`读取日志：${res.file}（慢 SQL 阈值 ${res.minDurationMs}ms）`, 2);
    } catch (e: unknown) {
      setSlowSqlError(getErrorMessage(e));
    } finally {
      setSlowSqlLoading(false);
    }
  };

  useEffect(() => {
    void refreshSlowReq();
    void refreshSlowSql();
  }, []);

  const slowReqTopColumns: ColumnsType<SlowRequestTopUrl> = useMemo(
    () => [
      { title: 'URL', dataIndex: 'url', key: 'url' },
      { title: '次数', dataIndex: 'count', key: 'count', width: 90, sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend' },
      { title: 'P50(ms)', dataIndex: 'p50', key: 'p50', width: 110 },
      { title: 'P90(ms)', dataIndex: 'p90', key: 'p90', width: 110 },
      { title: 'Max(ms)', dataIndex: 'maxMs', key: 'maxMs', width: 110 },
    ],
    [],
  );

  const slowReqRecentColumns: ColumnsType<SlowRequestItem> = useMemo(
    () => [
      { title: '时间', key: 'ts', width: 200, render: (_, r) => fmtTs(r.ts) },
      {
        title: '耗时',
        key: 'durationMs',
        width: 120,
        render: (_, r) => <Tag color={r.durationMs >= 3000 ? 'red' : 'orange'}>{r.durationMs}ms</Tag>,
      },
      { title: '方法', dataIndex: 'method', key: 'method', width: 90 },
      { title: 'URL', dataIndex: 'url', key: 'url' },
      { title: '状态码', dataIndex: 'statusCode', key: 'statusCode', width: 90 },
      { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140 },
      {
        title: '用户',
        key: 'userId',
        width: 90,
        render: (_, r) => (r.userId ? String(r.userId) : '-'),
      },
    ],
    [],
  );

  const slowSqlTopColumns: ColumnsType<SlowSqlTopQuery> = useMemo(
    () => [
      { title: 'SQL(前缀)', dataIndex: 'query', key: 'query' },
      { title: '次数', dataIndex: 'count', key: 'count', width: 90, sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend' },
      { title: 'Max(ms)', dataIndex: 'maxMs', key: 'maxMs', width: 110 },
    ],
    [],
  );

  const slowSqlRecentColumns: ColumnsType<SlowSqlItem> = useMemo(
    () => [
      { title: '时间', key: 'ts', width: 200, render: (_, r) => fmtTs(r.ts) },
      {
        title: '耗时',
        key: 'durationMs',
        width: 120,
        render: (_, r) => <Tag color={r.durationMs >= 1500 ? 'red' : 'orange'}>{r.durationMs}ms</Tag>,
      },
      { title: '参数数', dataIndex: 'paramsCount', key: 'paramsCount', width: 90 },
      { title: 'SQL', dataIndex: 'query', key: 'query' },
    ],
    [],
  );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>性能分析（慢请求 / 慢 SQL）</h2>

      <div style={{ marginBottom: 12 }}>
        <Alert
          type="info"
          showIcon
          message="说明"
          description={
            <div>
              <div>慢请求来源于后端输出的 [SLOWREQ] 日志；慢 SQL 来源于 TypeORM 的慢查询日志 [SLOWSQL]。</div>
              <Typography.Text type="secondary">数据按时间窗口从 PM2 out 日志中扫描聚合。</Typography.Text>
            </div>
          }
        />
      </div>

      <Row gutter={16}>
        <Col span={24}>
          <Card
            title="全局筛选"
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <span>时间窗口(分钟)</span>
                <InputNumber min={1} max={10080} value={minutes} onChange={(v) => setMinutes(Number(v || 60))} />
                <Button onClick={() => void refreshSlowReq()} loading={slowReqLoading}>
                  刷新慢请求
                </Button>
                <Button onClick={() => void refreshSlowSql()} loading={slowSqlLoading}>
                  刷新慢SQL
                </Button>
              </Space>
            }
          >
            <Space>
              <span>慢请求阈值(ms)</span>
              <InputNumber min={100} max={60000} value={slowReqMinMs} onChange={(v) => setSlowReqMinMs(Number(v || 1000))} />
              <span style={{ marginLeft: 12 }}>慢SQL阈值(ms)</span>
              <InputNumber min={10} max={60000} value={slowSqlMinMs} onChange={(v) => setSlowSqlMinMs(Number(v || 500))} />
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="慢请求 Top URL" style={{ marginBottom: 16 }}>
            {slowReqError ? <Alert type="error" showIcon message="获取失败" description={slowReqError} style={{ marginBottom: 12 }} /> : null}
            <Table
              rowKey="url"
              columns={slowReqTopColumns}
              dataSource={slowReqTopUrls}
              loading={slowReqLoading}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card title="慢请求明细（最近）" style={{ marginBottom: 16 }}>
            <Table
              rowKey={(r) => `${r.ts}-${r.ip}-${r.url}`}
              columns={slowReqRecentColumns}
              dataSource={slowReqRecent}
              loading={slowReqLoading}
              pagination={false}
              size="small"
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card title="慢 SQL Top" style={{ marginBottom: 16 }}>
            {slowSqlError ? <Alert type="error" showIcon message="获取失败" description={slowSqlError} style={{ marginBottom: 12 }} /> : null}
            <Table
              rowKey="query"
              columns={slowSqlTopColumns}
              dataSource={slowSqlTop}
              loading={slowSqlLoading}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card title="慢 SQL 明细（最近）">
            <Table
              rowKey={(r) => `${r.ts}-${r.durationMs}-${r.paramsCount}`}
              columns={slowSqlRecentColumns}
              dataSource={slowSqlRecent}
              loading={slowSqlLoading}
              pagination={false}
              size="small"
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Perf;

